import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Menu } from '@mantine/core';
import { isResource, resolveId, formatHumanName } from '@medplum/core';
import { Encounter, Patient, Practitioner, Provenance, QuestionnaireResponseItem, Task } from '@medplum/fhirtypes';
import { IconFileDownload } from '@tabler/icons-react';
import { pdf, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import {
  formatQuestionnaireAnswer,
  getQuestionnaireOutputs,
} from '../pages/PatientTimelinePage/services/questionnaireResponses';
import { alert } from '@/services/alert';
import { usePostHog } from 'posthog-js/react';
import { trackEncounterPDFDownloaded } from '@/services/analytics';
import React from 'react';
import { CHARTING_EXPORTED_PDF_TIMEZONE, EncounterStatus } from '@bariendo/lib/constants';
import everselfName from '@/assets/everself_bw.png';
import { formatDate, formatToAdobeSpectrumFormat, getTimestamp } from '@/services/date';
import { TimelineNote } from '@/components/Timeline/types';
import { formatBirthDate, formatGender } from '@/services/patient';

dayjs.extend(utc);
dayjs.extend(timezone);

interface EncounterDownloadButtonProps {
  encounter: Encounter & { tasks?: Task[]; history?: Provenance[] };
  tasks: Task[];
  notes?: TimelineNote[];
}

// PDF Styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  headerText: {
    padding: '1px 0',
  },
  headerTextLeft: {
    padding: '1px 0',
    marginLeft: 'auto',
  },
  headerLogo: {
    height: 24,
    width: 118,
    marginLeft: 'auto',
    marginBottom: 2,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 5,
    paddingBottom: 5,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 2,
    paddingBottom: 2,
  },
  label: {
    fontWeight: 'bold',
    width: '30%',
  },
  value: {
    width: '70%',
  },
  taskRow: {
    marginBottom: 2,
    padding: '5px 0',
  },
  taskTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  questionItem: {
    marginLeft: 10,
  },
  questionText: {
    fontWeight: 'bold',
    marginBottom: 2,
  },
  answerText: {
    marginLeft: 10,
    color: '#333',
  },
  addendumSection: {
    marginTop: 15,
    paddingTop: 10,
    borderTop: '1px solid #ccc',
  },
  addendumItem: {
    marginBottom: 8,
  },
  addendumHeader: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#555',
    marginBottom: 2,
  },
  addendumContent: {
    fontSize: 10,
    marginLeft: 10,
  },
});

// Recursive helper function to render questionnaire items and answers
function renderQuestionnaireItem(item: QuestionnaireResponseItem, titleDisplay?: string | null): React.ReactElement[] {
  const elements: React.ReactElement[] = [];

  // Render question text if it's not the same as the title
  if (item.text && item.text.toLowerCase()?.trim() !== titleDisplay?.toLowerCase()?.trim()) {
    elements.push(
      <View key={`text-${item.linkId}`} style={styles.questionItem}>
        <Text style={styles.questionText}>{item.text}</Text>
      </View>
    );
  }

  // Render answers
  if (item.answer && item.answer.length > 0) {
    item.answer.forEach((answer, answerIndex) => {
      const formattedAnswer = formatQuestionnaireAnswer(answer);
      if (formattedAnswer) {
        elements.push(
          <View key={`answer-${item.linkId}-${answerIndex}`} style={styles.questionItem}>
            <Text style={styles.answerText}>{formattedAnswer}</Text>
          </View>
        );
      }

      // Handle nested items in answers
      if (answer.item && answer.item.length > 0) {
        answer.item.forEach((nestedItem) => {
          elements.push(...renderQuestionnaireItem(nestedItem, titleDisplay));
        });
      }
    });
  }

  // Handle child items
  if (item.item && item.item.length > 0) {
    item.item.forEach((childItem) => {
      elements.push(...renderQuestionnaireItem(childItem, titleDisplay));
    });
  }

  return elements;
}

type EncounterPDFDocumentProps = {
  encounter: Encounter;
  tasks: Task[];
  title: string;
  notes?: TimelineNote[];
};

function PDFHeader({ encounter }: { encounter: Encounter }) {
  const practitioner = encounter.participant?.find((p) => isResource(p.individual?.resource, 'Practitioner'))
    ?.individual?.resource as Practitioner;
  const practitionerName = formatHumanName(practitioner?.name?.[0]);
  const practitionerEmail = practitioner?.telecom?.find((t) => t.system === 'email')?.value;

  return (
    <View style={styles.header} fixed>
      <View>
        <Text style={styles.headerText}>Date: {formatDate(new Date())}</Text>
        <Text style={styles.headerText}>Client: {encounter.subject?.display}</Text>
        <Text style={styles.headerText}>Gender: {formatGender((encounter.subject?.resource as Patient)?.gender)}</Text>
        <Text style={styles.headerText}>
          DOB & Age: {formatBirthDate((encounter.subject?.resource as Patient)?.birthDate)}
        </Text>
      </View>
      <View>
        <Image style={styles.headerLogo} src={everselfName} />
        <Text style={styles.headerTextLeft}>Everself (formerly Bariendo)</Text>
        {practitionerEmail && <Text style={styles.headerTextLeft}>{practitionerEmail}</Text>}
        <Text style={styles.headerTextLeft}>{practitionerName}</Text>
        <Text style={styles.headerTextLeft}>945 Market St Suite 501 San Francisco CA 94103</Text>
      </View>
    </View>
  );
}

function PDFContent({ tasks }: { tasks: Task[] }) {
  const questionnaireOutputs = getQuestionnaireOutputs(tasks);

  return (
    <>
      {questionnaireOutputs.length > 0 && (
        <View>
          {questionnaireOutputs.map(({ key, display, response }) => (
            <View key={key} style={styles.taskRow}>
              <Text style={styles.taskTitle}>{display || 'Task Response'}</Text>
              {response.item?.map((item) => (
                <View key={`${response.id ?? key}-${item.linkId}`}>{renderQuestionnaireItem(item, display)}</View>
              ))}
            </View>
          ))}
        </View>
      )}
    </>
  );
}

function PDFSignature({ encounter }: { encounter: Encounter & { history?: Provenance[] } }) {
  const signature = encounter.history?.find((item) => item.reason?.[0]?.coding?.[0]?.code === 'SIGN');

  const agent = signature?.agent?.[0].who?.display;
  const date = signature?.recorded;

  // Convert to EDT timezone
  let signatureDate: dayjs.Dayjs | undefined;
  if (date) {
    signatureDate = dayjs(date).tz(CHARTING_EXPORTED_PDF_TIMEZONE);
  }

  return (
    <>
      {agent && <Text style={{ fontWeight: 'bold' }}>Signed by {agent}</Text>}
      {signatureDate && <Text>Date: {formatToAdobeSpectrumFormat(signatureDate)} EDT</Text>}
    </>
  );
}

function getNoteSenderName(note: TimelineNote): string {
  const sender = note.sender;
  if (!sender) return 'Unknown';
  if (sender.resourceType === 'Organization') return sender.name;
  return formatHumanName(sender.name?.[0]) || 'Unknown';
}

function PDFAddendums({ notes }: { notes?: TimelineNote[] }) {
  if (!notes || notes.length === 0) return null;

  return (
    <View style={styles.addendumSection}>
      <Text style={styles.sectionTitle}>Addendums</Text>
      {notes.map((note, index) => (
        <View key={index} style={styles.addendumItem}>
          <Text style={styles.addendumHeader}>
            Addended by {getNoteSenderName(note)} — {getTimestamp(note)}
          </Text>
          {note.content && <Text style={styles.addendumContent}>{note.content}</Text>}
        </View>
      ))}
    </View>
  );
}

// PDF Document Component
function EncounterPDFDocument({ encounter, tasks, title, notes }: EncounterPDFDocumentProps) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <PDFHeader encounter={encounter} />
        <Text style={styles.title}>{title}</Text>
        <PDFContent tasks={tasks} />
        <PDFSignature encounter={encounter} />
        <PDFAddendums notes={notes} />
      </Page>
    </Document>
  );
}

export function EncounterDownloadButton({ encounter, tasks, notes }: EncounterDownloadButtonProps) {
  const posthog = usePostHog();

  const handleDownload = async () => {
    try {
      const title = encounter.tasks?.[0]?.basedOn?.[0]?.display ?? 'Chart note';

      const formattedTitle = title.split(' - ')[1];

      const doc = <EncounterPDFDocument encounter={encounter} tasks={tasks} title={formattedTitle} notes={notes} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);

      const filename = `encounter-${encounter.id || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      // Track PDF download
      if (encounter.id) {
        const patientId = encounter.subject ? resolveId(encounter.subject) : '';
        trackEncounterPDFDownloaded(posthog, {
          encounter_id: encounter.id,
          patient_id: patientId,
          tasks_count: tasks.length,
        });
      }
    } catch (error) {
      alert.error({
        title: "Couldn't download PDF",
        message: error instanceof Error ? error.message : 'Please try again or contact support.',
      });
    }
  };

  if (encounter.status !== EncounterStatus.FINISHED) return null;

  return (
    <Menu.Item leftSection={<IconFileDownload size={18} color="#246A7A" />} onClick={handleDownload}>
      Download PDF
    </Menu.Item>
  );
}
